/**
 * Hi! Welcome to the Phaneroo Prayer Requests replies store.
 *
 * - All API fetch requests are actually handled inside the `replies.service.js` file.
 * - Every action has a corresponding $axios request that performs validation and request handling.
 * - Tests for this store module are located in './tests/replies.test.js'
 *
 * @todo
 * - Perform data transforms for dashboard UI
 * - Write tests for replies store.
 * - Require collect token in $axios instance from session storage
 *   instead of passing it as param.
 */

import '../../typedefs'
import assign from 'lodash-es/assign'
import { getRepliesById, getReplyById, createReply, updateReply, deleteReply } from '../../services/replies.service'
import { indexArrayBy } from '../../utils'

const __DEV__ = process.env.NODE_ENV !== 'production'

export const baseRepliesState = {
  /**
   * @type {Reply[]}
   */
  replies: [],
  /**
   * @type {Reply}
   */
  reply: null,
  /**
   * @type {User}
   */
  authors: null,
  /**
   * @type {Boolean}
   */
  isLoadingReplies: false
}

export default {
  namespaced: true,
  state: baseRepliesState,
  getters: {
    /**
     * @returns {Reply[]}
     */
    replies: state => state.replies
      .map(reply => {
        return {
          ...reply,
          ...state.authors && { author: state.authors[reply.user_id] }
        }
      })
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    isLoadingReplies: state => state.isLoadingReplies
  },
  mutations: {
    /**
     * Sets array of prayer request replies to active state
     * @param {Object} state state
     * @param {Reply[]} payload  Prayer request replies array
     */
    SET_REPLIES (state, payload) {
      state.replies = payload
    },
    /**
     * Clears array of prayer request replies from active state
     * @param {Object} state state
     * @param {Reply[]} payload  Prayer request replies array
     */
    CLEAR_REPLIES (state) {
      state.replies = []
    },
    /**
     * Sets all prayer request authors
     * @param {Object} state state
     * @param {Reply[]} payload  Prayer request replies array
     */
    SET_REPLIES_AUTHORS (state, payload) {
      state.authors = payload
    },
    /**
     * Sets specific prayer request reply to active state
     * @param {Object} state state
     * @param {Reply} payload Prayer request reply object
     */
    SET_REPLY (state, payload) {
      state.reply = payload
    },
    /**
     * Adds new prayer request reply to active state
     * @param {Object} state state
     * @param {Reply} payload New prayer request reply
     */
    ADD_REPLY (state, payload) {
      state.replies.push(payload)
    },
    /**
     * Updates specific prayer requests reply's object in state
     * @param {Object} state state
     * @param {Reply} payload Reply
     */
    UPDATE_REPLY (state, payload) {
      const targetIndex = state.replies.findIndex(reply => reply.id === payload.id)
      assign(state.replies[targetIndex], payload)
    },
    /**
     * Deletes specific prayer request reply from state
     * @param {Object} state state
     * @param {String} id Target reply id
     */
    DELETE_REPLY (state, id) {
      const targetIndex = state.replies.findIndex(reply => reply.id === id)
      state.replies.splice(targetIndex, 1)
    },
    /**
     * Resets the original state for prayer requests
     * @param {Object} state state
     */
    RESET_REPLIES (state) {
      // state.replies = baseRepliesState.replies
      // state.reply = baseRepliesState.reply
      // state.authors = baseRepliesState.authors
      assign(state, baseRepliesState)
    },
    /**
     * Resets the original state for prayer requests
     * @param {Object} state state
     */
    SET_LOADING_STATUS (state, payload) {
      state.isLoadingReplies = payload
    }
  },
  actions: {
    /**
     * Gets all replies for a prayer request
     * @param {VuexContext} context Store context
     */
    async getRepliesById ({ commit, rootGetters, rootState }, id) {
      const authToken = rootState.auth.authToken
      commit('SET_LOADING_STATUS', true)
      const response = await getRepliesById(authToken, id)
      const { data: replies } = response.data
      const commentAuthorIds = [ ...new Set(replies.map(reply => reply.user_id)) ]

      const filteredAuthors = commentAuthorIds.map((userId) => {
        const author = rootGetters['auth/user']
        return author
      })

      const authors = indexArrayBy(filteredAuthors, 'id')
      commit('SET_REPLIES', replies)
      commit('SET_REPLIES_AUTHORS', authors)
      commit('SET_LOADING_STATUS', false)
    },
    /**
     * Gets a single prayer request reply
     * @param {VuexContext} context
     * @param {String} id Reply Id
     */
    async getReplyById ({ commit, rootGetters, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await getReplyById(authToken, id)
      const { data: reply } = response.data
      const author = rootGetters['auth/user']
      commit('SET_REPLY', { ...reply, author })
    },
    /**
     * Creates a new prayer request reply
     * @param {VuexCategory} context Store context
     * @param {Reply} payload new prayer request reply data
     */
    async createReply ({ commit, rootGetters, rootState }, payload) {
      const authToken = rootState.auth.authToken
      const response = await createReply(authToken, payload)
      const { data: reply } = response.data
      const author = rootGetters['auth/user']
      commit('ADD_REPLY', { ...reply, author })
    },
    /**
     * Updates a prayer request
     * @param {VuexCategory} context Store context
     * @param {{ id: String, payload: PrayerRequest }} payload Category data
     */
    async updateReply ({ commit, rootGetters, rootState }, { id, payload }) {
      const authToken = rootState.auth.authToken
      const response = await updateReply(authToken, id, payload)
      const { data: reply } = response.data
      const author = rootGetters['auth/user']
      commit('UPDATE_REPLY', { ...reply, author })
    },
    /**
     * Deletes a prayer request
     * @param {VuexCategory} context Store context
     * @param {String} id prayer request id
     */
    async deleteReply ({ commit, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await deleteReply(authToken, id)
      const { message } = response
      __DEV__ && console.log(`Deleted reply of id: ${id}:`, message)
      commit('DELETE_REPLY', id)
    }
  }
}
